/**
 * The value entry of the "in list" / "not in list" filter operators: the
 * values already in the list as small removable items, then one entry field of
 * the column's own kind — `FilterValueInput`, so a number column takes numbers
 * and a date column the date picker, exactly like the single-value operators.
 *
 * - Enter in the entry field adds its value to the list; Enter on an empty
 *   entry goes to the chip (apply). Backspace on an empty entry removes the
 *   last value.
 * - Pasting text that holds several values (one per line, or separated by
 *   tabs or commas) adds each of them. On a number column, a number spelled
 *   past the digit limit is not added; a note next to the entry names it.
 * - The value being typed is already part of `values` (the chip's condition),
 *   so Apply never drops it: `values` ends with it while the entry is not empty.
 *
 * Values are kept as the emitters take them (`string | number`, deduplicated):
 * a number column's entries are numbers (unquoted in SQL), everything else is
 * text.
 */
import { forwardRef, useCallback, useState } from 'react';
import { X } from 'lucide-react';

import { FilterValueInput } from './FilterValueInput';
import { formatFilterValue, type DataTypeCategory } from '../../utils/filterUtils';
import { parseListEntries, toListValue, uniqueListValues, type ListValue } from '../../utils/filterValueList';

type FilterValueListInputProps = {
  values: readonly ListValue[];
  onChange: (values: ListValue[]) => void;
  dataTypeCategory: DataTypeCategory;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  autoFocus?: boolean;
};

/** Values shown before the rest fold into "+N more". */
const VISIBLE_VALUES = 6;

type Entry = string | number | bigint | boolean | Date | null;

export const FilterValueListInput = forwardRef<HTMLInputElement, FilterValueListInputProps>(
  function FilterValueListInput({ values, onChange, dataTypeCategory, onKeyDown, autoFocus = false }, ref) {
    const [entry, setEntry] = useState<Entry>(null);
    const [expanded, setExpanded] = useState(false);
    // Pasted numbers the list refused (`parseListEntries`), until the next entry.
    const [refused, setRefused] = useState<string[]>([]);

    const pending = toListValue(entry, dataTypeCategory);
    // The value being typed rides at the end of `values`; anything else is a list item.
    const typing = pending !== null && values.length > 0 && values[values.length - 1] === pending;
    const items = typing ? values.slice(0, -1) : values;

    const handleEntryChange = useCallback(
      (next: Entry) => {
        setEntry(next);
        setRefused([]);
        const value = toListValue(next, dataTypeCategory);
        onChange(value === null ? [...items] : [...items, value]);
      },
      [dataTypeCategory, items, onChange],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && pending !== null) {
          e.preventDefault();
          e.stopPropagation();
          setEntry(null);
          onChange(uniqueListValues([...items, pending]));
          return;
        }
        if (e.key === 'Backspace' && (entry === null || entry === '') && items.length > 0) {
          e.preventDefault();
          onChange(items.slice(0, -1));
          return;
        }
        onKeyDown?.(e);
      },
      [entry, items, onChange, onKeyDown, pending],
    );

    const handlePaste = useCallback(
      (e: React.ClipboardEvent) => {
        const pasted = parseListEntries(e.clipboardData.getData('text'), dataTypeCategory);
        if (pasted === null) return; // one value: the field takes it as typed
        e.preventDefault();
        setEntry(null);
        setRefused(pasted.refused);
        onChange(uniqueListValues([...items, ...pasted.values]));
      },
      [dataTypeCategory, items, onChange],
    );

    const shown = expanded ? items : items.slice(0, VISIBLE_VALUES);
    const hidden = items.length - shown.length;

    return (
      <span
        className="inline-flex max-w-md flex-wrap items-center gap-1"
        data-testid="filter-value-list"
        data-value-count={items.length}
      >
        <span className={`inline-flex flex-wrap items-center gap-1 ${expanded ? 'max-h-32 overflow-y-auto' : ''}`}>
          {shown.map((value, index) => (
            <span
              key={`${index}:${String(value)}`}
              className="inline-flex items-center gap-0.5 rounded border border-input bg-background px-1.5 py-0.5 text-xs"
              data-testid="filter-value-list-item"
            >
              <span translate="no">{formatFilterValue(value)}</span>
              <button
                type="button"
                onClick={() => onChange(values.filter((_, i) => i !== index))}
                className="rounded text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${formatFilterValue(value)}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))}
          {(hidden > 0 || expanded) && items.length > VISIBLE_VALUES && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              data-testid="filter-value-list-more"
            >
              {expanded ? 'Show fewer' : `+${hidden} more`}
            </button>
          )}
        </span>
        <span onPaste={handlePaste}>
          <FilterValueInput
            ref={ref}
            value={entry}
            onChange={handleEntryChange}
            onKeyDown={handleKeyDown}
            dataTypeCategory={dataTypeCategory}
            placeholder={items.length === 0 ? 'Type or paste values' : 'Add value'}
            className={items.length === 0 ? 'w-36' : 'w-24'}
            autoFocus={autoFocus}
            aria-label="Add a value to the list"
          />
        </span>
        {refused.length > 0 && (
          <span className="text-xs text-destructive" data-testid="filter-value-list-refused" role="status">
            Not added, too many digits: {refused.join(', ')}
          </span>
        )}
      </span>
    );
  },
);
